/**
 * 원클릭 PDF (Pro) — 클라이언트 측 어댑터.
 *
 * 흐름:
 *   1. 현재 프리뷰 DOM을 인쇄용 HTML 문서로 스냅샷 (KaTeX/highlight.js/Pretendard
 *      스타일이 함께 임베드되도록 head의 <link rel=stylesheet>를 그대로 복사).
 *   2. /api/pdf로 POST. 응답이 application/pdf면 Blob 다운로드 트리거.
 *   3. Cloudflare Access가 미인증을 잡으면 302가 떨어지는데, fetch에서
 *      redirect:'manual'로 잡아내 새 탭에서 OTP 로그인을 안내한다.
 *
 * /api/pdf는 functions/api/pdf.ts가 처리, Cloudflare Access의 family-access
 * 정책으로 보호 (가족 3명만 통과).
 */

export interface ProPdfDeps {
  preview: HTMLElement;
  onProgress: (message: string) => void;
}

function buildPdfHtml(preview: HTMLElement): string {
  const origin = window.location.origin;

  // 빌드된 CSS 링크와 인라인 <style>을 그대로 복사 — KaTeX, highlight.js,
  // preview.css, print.css가 한 번에 적용된다. <base>가 있어서 hashed
  // assets 경로(/assets/index-XXX.css)가 puppeteer 측에서 정상 해석된다.
  const headBits: string[] = ['<meta charset="UTF-8">', `<base href="${origin}/">`];
  document.head.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
    headBits.push((l as HTMLLinkElement).outerHTML);
  });
  document.head.querySelectorAll('style').forEach((s) => {
    headBits.push(s.outerHTML);
  });

  // Pretendard 임베드 보장 — 폰트 link만 preload로 들어가 있어서 puppeteer
  // 컨텍스트에서 안 끌어다 쓸 수 있으니 명시적 @font-face도 한 번 더 깔아둔다.
  headBits.push(`<style>
    body { margin: 0; padding: 24mm 0; font-family: 'Pretendard', system-ui, sans-serif; color: #111827; }
    .preview { padding: 0 !important; max-width: none !important; margin: 0 !important; }
  </style>`);

  return `<!doctype html>
<html lang="ko">
<head>
${headBits.join('\n')}
</head>
<body>
<article class="preview">${preview.innerHTML}</article>
</body>
</html>`;
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { message?: string; error?: string };
    return data.message || data.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 다음 tick에서 revoke — 일부 브라우저에서 click 직후 revoke하면 download 취소되는 케이스 회피.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadProPdf({ preview, onProgress }: ProPdfDeps): Promise<void> {
  if (!preview.children.length) {
    onProgress('먼저 마크다운을 입력해주세요.');
    return;
  }

  onProgress('원클릭 PDF 생성 중...');

  let res: Response;
  try {
    res = await fetch('/api/pdf', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ html: buildPdfHtml(preview), filename: 'mdview.pdf' }),
      redirect: 'manual',
      credentials: 'include',
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : '알 수 없는 오류';
    onProgress(`PDF 생성 실패: ${msg}`);
    return;
  }

  if (res.type === 'opaqueredirect' || res.status === 0) {
    // Cloudflare Access 미인증 — 새 탭에서 OTP 로그인 유도.
    // 새 탭의 /api/pdf GET 응답(HTML)이 BroadcastChannel로 'auth-done'을 발송하면
    // 메인 탭은 자동으로 fetch를 다시 시도해 PDF를 받는다. 사용자는 버튼을 두 번
    // 누를 필요가 없다.
    onProgress('가족 인증 페이지를 열었어요. 인증 후 자동으로 다운로드됩니다.');

    // 채널 셋업은 새 탭 열기 *전에* — 메시지를 놓치지 않도록.
    let retried = false;
    if ('BroadcastChannel' in window) {
      const ch = new BroadcastChannel('mdview-pdf-auth');
      const cleanup = (): void => {
        ch.close();
      };
      ch.addEventListener('message', (ev) => {
        if (retried) return;
        if ((ev.data as { type?: string } | null)?.type !== 'auth-done') return;
        retried = true;
        cleanup();
        // 새 탭이 인증을 끝낸 시점 — 메인 탭에서 fetch 재시도.
        void downloadProPdf({ preview, onProgress });
      });
      // 안전망: 5분 후 채널 정리 (사용자가 인증 안 하고 잊었을 수 있음).
      window.setTimeout(cleanup, 5 * 60 * 1000);
    }

    window.open('/api/pdf', '_blank', 'noopener');
    return;
  }

  if (!res.ok) {
    const msg = await readErrorMessage(res);
    // 429(rate limit)는 일시적 — 사용자에게 raw 메시지 대신 친화 안내만 노출.
    if (res.status === 429) {
      onProgress(msg);
      return;
    }
    onProgress(`PDF 실패: ${msg}`);
    return;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/pdf')) {
    onProgress('서버 응답이 PDF 형식이 아닙니다.');
    return;
  }

  const blob = await res.blob();
  triggerDownload(blob, 'mdview.pdf');
  onProgress('PDF 저장됨');
}
